/**
 * THE barcode dictionary. One file, every type the app can read.
 *
 * Two families, and only one of them is ours to name:
 *
 * - RECOGNISED (origin 'gs1' / 'shape'). Printed by the outside world to a
 *   standard. `00` and `01` are GS1 Application Identifiers — AI(00) is an
 *   SSCC, AI(01) a GTIN — not prefixes anyone here chose. Their prefix is part
 *   of the identifier the API stores, so it is KEPT (`keepPrefix`). A label may
 *   print the AI in GS1's human-readable form, `(00)123…`, which is the same
 *   identifier with brackets round the AI — accepted, and normalised back.
 * - ASSIGNED (origin 'internal'). Printed by us, so we pick the prefix. Always
 *   letters in PARENTHESES: a GS1 element string is all digits and an ISO 6346
 *   container is 11 alphanumerics, so a bracketed alpha code cannot be mistaken
 *   for either — and bracketed LETTERS cannot be mistaken for a bracketed AI,
 *   which is always digits. The prefix is scaffolding and is STRIPPED.
 *
 * A type may declare SEVERAL prefixes, which is how a label reprint happens
 * without a flag day: doors read a leading space on the signs still up and
 * `(DR)` on the reprinted ones, and both work meanwhile. An empty string ''
 * means "a bare value can be this type" — the catch-all every workflow screen
 * already relies on. Longest prefix wins, so '' can never swallow a code
 * another type claims.
 */

export type ScanType =
  | 'pallet' | 'box' | 'container'
  | 'door' | 'gang' | 'trip' | 'location'
  | 'username' | 'password' | 'employee'

export type ScanTypeDef = {
  type: ScanType
  /** What an operator calls it — field labels come from here. */
  label: string
  /** Every prefix meaning this type, '' for a bare value. Order is irrelevant. */
  prefixes: string[]
  origin: 'gs1' | 'internal' | 'shape'
  /**
   * Whether the prefix belongs to the VALUE. GS1 identifiers carry theirs
   * (the API stores `00…`), our own prefixes are scaffolding and come off.
   */
  keepPrefix: boolean
  placeholder: string
  example: string
  /** For standards recognised by shape rather than by a prefix. */
  matches?: (value: string) => boolean
  /** Canonical form of a value, where the standard has one. */
  normalize?: (value: string) => string
}

/** ISO 6346: four letters then seven digits, e.g. TEMU9477073. */
function isContainerCode(value: string): boolean {
  return /^[A-Z]{4}\d{7}$/.test(value.toUpperCase())
}

/**
 * `(00)123…` and `00123…` are the same SSCC.
 *
 * GS1's human-readable form brackets the Application Identifier; the brackets
 * are printing, not data, and the API stores the identifier with its AI and
 * without them. So the brackets come off while the AI digits stay — which is
 * why this is a normalize and not a stripped prefix.
 */
function unbracketAi(value: string): string {
  return value.replace(/^\((\d{2})\)/, '$1')
}

export const SCAN_TYPES: Record<ScanType, ScanTypeDef> = {
  pallet: {
    type: 'pallet',
    label: 'Pallet',
    prefixes: ['00', '(00)'],
    origin: 'gs1',
    keepPrefix: true,
    placeholder: '00…',
    example: '00174011161061630309',
    normalize: unbracketAi,
  },
  box: {
    type: 'box',
    label: 'Box',
    prefixes: ['01', '(01)'],
    origin: 'gs1',
    keepPrefix: true,
    placeholder: '01…',
    example: '0100761234567890',
    normalize: unbracketAi,
  },
  container: {
    type: 'container',
    label: 'Container',
    prefixes: [],
    origin: 'shape',
    keepPrefix: true,
    placeholder: 'MSCU1234567',
    example: 'TEMU9477073',
    matches: isContainerCode,
    normalize: value => value.toUpperCase(),
  },
  door: {
    type: 'door',
    // A leading space is what the door signs still up carry; (DR) is what the
    // reprinted ones will. Bare stays accepted so a scuffed label still works.
    prefixes: ['(DR)', ' ', ''],
    label: 'Door Number',
    origin: 'internal',
    keepPrefix: false,
    placeholder: 'Scan the door to send',
    example: '(DR)12',
  },
  gang: {
    type: 'gang',
    label: 'Gang',
    prefixes: ['(GN)', ''],
    origin: 'internal',
    keepPrefix: false,
    placeholder: 'Scan your gang',
    example: '(GN)2',
  },
  trip: {
    type: 'trip',
    label: 'Trip Number',
    prefixes: ['(TP)', ''],
    origin: 'internal',
    keepPrefix: false,
    placeholder: 'Scan or type the trip',
    example: '(TP)03708532',
  },
  location: {
    type: 'location',
    label: 'Inventory Location',
    prefixes: ['(LC)', ''],
    origin: 'internal',
    keepPrefix: false,
    placeholder: 'Scan or type a location',
    example: '(LC)RM4FLR1',
  },
  username: {
    type: 'username',
    label: 'Username',
    // No bare form: on the sign-in screen an unprefixed scan falls back to
    // "the first empty box", which is the behaviour badge cards rely on today.
    prefixes: ['(US)'],
    origin: 'internal',
    keepPrefix: false,
    placeholder: '',
    example: '(US)first.last',
  },
  password: {
    type: 'password',
    label: 'Password',
    prefixes: ['(PW)'],
    origin: 'internal',
    keepPrefix: false,
    placeholder: '',
    example: '(PW)hunter2',
  },
  employee: {
    type: 'employee',
    label: 'Employee ID',
    prefixes: ['(EM)', ''],
    origin: 'internal',
    keepPrefix: false,
    placeholder: 'Employee ID',
    example: '(EM)1021',
  },
}

export type ScanMatch = { type: ScanType, value: string }

/**
 * Scanners and terminators add trailing whitespace; a LEADING space is a real
 * door prefix and must survive to be matched. Nothing else may trim a scan
 * before it reaches here.
 */
export function tidyScan(raw: string): string {
  return raw.replace(/\s+$/, '')
}

function prefixHit(raw: string, prefix: string): boolean {
  return prefix !== '' && raw.toUpperCase().startsWith(prefix.toUpperCase())
}

function take(def: ScanTypeDef, raw: string, prefix: string): ScanMatch {
  const value = (def.keepPrefix ? raw : raw.slice(prefix.length)).trim()
  return { type: def.type, value: def.normalize ? def.normalize(value) : value }
}

/** A prefix that identifies a type ANYWHERE, not just where one is expected. */
function isStrong(prefix: string): boolean {
  return prefix.trim() !== ''
}

export type MatchOptions = {
  /** Let an unprefixed code fall to a type declaring ''. Default true. */
  allowBare?: boolean
  /** Honour a whitespace-only prefix (the legacy door label). Default true. */
  allowWeakPrefixes?: boolean
}

/**
 * Read a scan as one of `allowed`, in order of how specific the claim is:
 * an explicit prefix, then a recognised shape, then a bare value.
 *
 * Screens pass the types they can accept, which is what makes a bare code
 * unambiguous — the same `2` is a door on Load Truck and a gang on Home
 * because those screens allow different things. A prefixed code needs no such
 * help and means the same thing everywhere.
 */
export function matchScan(raw: string, allowed: ScanType[], options: MatchOptions = {}): ScanMatch | null {
  const code = tidyScan(raw)
  if (code.trim() === '') return null

  const defs = allowed.map(type => SCAN_TYPES[type])

  const claims = (strong: boolean) => defs
    .flatMap(def => def.prefixes.map(prefix => ({ def, prefix })))
    .filter(claim => claim.prefix !== '' && isStrong(claim.prefix) === strong)
    // Longest first, so '(DR)' beats a bare space and '(00)' beats '00'.
    .sort((a, b) => b.prefix.length - a.prefix.length)

  const hit = (claim: { def: ScanTypeDef, prefix: string }) =>
    // A code that is ONLY the prefix carries no value: bare '00' is not a
    // pallet, it is two characters that happen to look like an AI.
    code.length > claim.prefix.length && prefixHit(code, claim.prefix)

  for (const claim of claims(true)) {
    if (hit(claim)) return take(claim.def, code, claim.prefix)
  }

  // Standards outrank a whitespace heuristic: ' TEMU9477073' is a container
  // that picked up a stray space, not a door called TEMU9477073.
  const byShape = defs.find(def => def.matches?.(code.trim()))
  if (byShape) return take(byShape, code, '')

  if (options.allowWeakPrefixes !== false) {
    for (const claim of claims(false)) {
      if (hit(claim)) return take(claim.def, code, claim.prefix)
    }
  }

  // A code that is exactly a prefix carries no value, and the bare fallback
  // below would otherwise catch it — filing a half-read '(DR)12' as a door
  // literally named '(DR)'. The strong-prefix guard above only refuses to read
  // it AS a prefix; this refuses to read it at all.
  const onlyAPrefix = defs.some(def => def.prefixes.some(prefix =>
    prefix.trim() !== '' && prefix.toUpperCase() === code.toUpperCase()))
  if (onlyAPrefix) return null

  if (options.allowBare === false) return null

  const bare = defs.filter(def => def.prefixes.includes(''))
  // More than one bare claimant is a screen configuring itself ambiguously;
  // the first listed wins, so callers order `allowed` by what they expect most.
  return bare.length > 0 ? take(bare[0]!, code, '') : null
}

/**
 * Read a scan without a screen's help. Only definite claims count: a strong
 * prefix or a recognised standard.
 *
 * The legacy leading-space door prefix is deliberately NOT definite. Stray
 * whitespace is far too easy to acquire, and a location scanned as " RM4FLR1"
 * on the move screen must not be declared a door — where a door is actually
 * expected, matchScan still honours it.
 */
export function identifyScan(raw: string): ScanMatch | null {
  const all = Object.keys(SCAN_TYPES) as ScanType[]
  const definite = all.filter(type =>
    SCAN_TYPES[type].prefixes.some(isStrong) || SCAN_TYPES[type].matches)
  return matchScan(raw, definite, { allowBare: false, allowWeakPrefixes: false })
}

/**
 * A code that definitely belongs to some OTHER type than the ones a screen
 * accepts — so the screen can say "that is a gang label" instead of quietly
 * filing it in whichever box takes bare values.
 */
export function misdirectedScan(raw: string, allowed: ScanType[]): ScanType | null {
  const definite = identifyScan(raw)
  if (!definite || allowed.includes(definite.type)) return null
  return definite.type
}

/** The canonical printed form of a value — what a label generator should encode. */
export function encodeLabel(type: ScanType, value: string): string {
  const def = SCAN_TYPES[type]
  if (def.keepPrefix) return value
  const prefix = def.prefixes.find(p => p.trim() !== '') ?? ''
  return `${prefix}${value}`
}
